import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Stack, Typography, styled } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ModalsWrapper from 'components/general/dialogs/dialog-wrapper'
import { OrderTableItem } from './models/order'
import { useGetPropertyDelayedFeesQuery } from 'redux/apis/interest-rates-api'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { PropertyDelayedFeesDialogHeader } from './components/property-delayed-fees-dialog-header'
import { useForm, useWatch } from 'react-hook-form'
import dayjs from 'dayjs'
import { PropertyDelayedFeesItem } from './components/property-delayed-fees-item'
import { useAccessToken, useAuthContext } from 'components/auth/auth.slice'
import { PropertyDelayedFeesDetailedView } from './components/property-delayed-fees-detailed-view'
import { Config } from 'redux/config'
import { toast } from 'react-toastify'
import { getParentPropertyCadastreNr } from 'utils/cadastre-utils'

const CUBIT_COMPANY_ID = '637ca7af6e58905181666b3d'

export type FeesForm = {
    endDate: string | Date
}

type PropertyDelayedFeesDialogProps = {
    isOpen: boolean
    onClose: () => void
    orderLine: OrderTableItem | undefined
}

export const PropertyDelayedFeesDialog: React.FC<PropertyDelayedFeesDialogProps> = ({ isOpen, onClose, orderLine }) => {
    const { t } = useTranslation()
    const ctx = useAuthContext()
    const token = useAccessToken()

    const { control, reset } = useForm<FeesForm>({ defaultValues: { endDate: new Date() } })
    const endDate = useWatch({ control, name: 'endDate' })

    const [isDetailedview, setIsDetailedView] = React.useState(false)
    const [isDownloadingPdf, setIsDownloadingPdf] = React.useState(false)
    const [isSectionParentView, setIsSectionParentView] = React.useState(false)

    const { data, isSuccess, isFetching } = useGetPropertyDelayedFeesQuery(
        !orderLine
            ? skipToken
            : {
                  orderId: orderLine.orderId,
                  orderLineId: orderLine.uniqueId,
                  endDate: dayjs(endDate).format('YYYY-MM-DD'),
                  targetMainProperty: isSectionParentView,
              },
    )

    React.useEffect(() => {
        reset()
        setIsSectionParentView(false)
        setIsDetailedView(false)
    }, [isOpen, reset])

    if (!orderLine) return null

    const getPdfUrl = async () => {
        if (!token) throw new Error('token must be provided')

        setIsDownloadingPdf(true)

        try {
            const pdfDownloadUrl = `${Config.backendUrl}/v1/InterestRates/property-delayed-fees/pdf`
            const query = `?orderId=${orderLine.orderId}&orderLineId=${orderLine.uniqueId}&endDate=${dayjs(
                endDate,
            ).format('YYYY-MM-DD')}&targetMainProperty=${isSectionParentView}`

            const res = await fetch(`${pdfDownloadUrl}${query}`, {
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            })

            const blob = await res.blob()
            const url = URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = url
            a.download = 'Eiendom-forsinkelsesrenter.pdf' // Change to your preferred filename
            a.download = `Eiendom-forsinkelsesrenter_${cadastreNr}.pdf` // Change to your preferred filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            // Release memory
            URL.revokeObjectURL(url)
        } catch (err) {
            toast(t('TEXT_SOMETHING_WENT_WRONG', 'Something went wrong. Try again.'), { type: 'error' })
        } finally {
            setIsDownloadingPdf(false)
        }
    }

    const getNoResultsText = () => {
        if (orderLine.propertyType === 'SectionedProperty' && !isSectionParentView) {
            return t('TEXT_NO_DELAYED_FEES_FOR_SECTION_CHILD', 'Ingen  påløpte forsinkelsesrenter på seksjonen')
        }

        return t('TEXT_NO_DELAYED_FEES_FOR_SECTION', 'Ingen  påløpte forsinkelsesrenter')
    }

    const minDate = Math.min(...(data?.delays?.map((fee) => new Date(fee.invoice.dueDate).getTime()) || []))
    const cadastreNr = isSectionParentView ? getParentPropertyCadastreNr(orderLine.cadastreNr) : orderLine.cadastreNr

    return (
        <ModalsWrapper
            open={isOpen}
            title={
                <span>
                    {t('TEXT_DELAYED_FEES_DIALOG_TITLE', 'Delayed interest fees for')} {cadastreNr}
                </span>
            } // not using i18n interpolation because it fks up cadastre nr
            handleClose={onClose}
            fullWidth
            maxWidth="md"
            isLoading={isFetching}
            body={
                <div>
                    <PropertyDelayedFeesDialogHeader
                        control={control}
                        totalFees={data?.delays?.reduce((acc, curr) => acc + curr.totalInterestAccrued, 0) || 0}
                        from={new Date(minDate)}
                    />
                    {data?.isWithSentDebt === true && (
                        <Typography>
                            {t(
                                'TEXT_DELAYED_FEES_HAS_SENT_TO_DEBT',
                                'Vi presiserer at det er restanser for tidligere år, og det har påløpt forsinkelsesrenter og eventuelle omkostninger som ikke kommer frem i denne oversikten.',
                            )}
                        </Typography>
                    )}
                    {ctx?.entityId === CUBIT_COMPANY_ID && (
                        <Button
                            color="primary"
                            variant="contained"
                            sx={{ my: 2 }}
                            onClick={() => setIsDetailedView((prev) => !prev)}
                        >
                            Toggle detailed view
                        </Button>
                    )}
                    {isDetailedview && (
                        <PropertyDelayedFeesDetailedView
                            endDate={endDate}
                            orderLine={orderLine}
                            isSectionParentView={isSectionParentView}
                        />
                    )}

                    {isSuccess && !isFetching && data?.delays.length === 0 && !isDetailedview && (
                        <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                            <Typography variant="h6">{getNoResultsText()}</Typography>
                        </Box>
                    )}

                    {isSuccess && !isFetching && data?.delays.length !== 0 && !isDetailedview && (
                        <Box display="flex" flexDirection="column" gap={2}>
                            {data.delays.map((delayed) => (
                                <PropertyDelayedFeesItem key={delayed.invoice.invoiceNr} feeResult={delayed} />
                            ))}
                        </Box>
                    )}
                </div>
            }
            actions={
                (data?.delays?.length !== 0 || orderLine.propertyType === 'SectionedProperty') && (
                    <ActionWrapper>
                        {orderLine.propertyType === 'SectionedProperty' && (
                            <LoadingButton
                                disableElevation
                                variant="outlined"
                                color="accent"
                                type="button"
                                loading={isDownloadingPdf}
                                onClick={() => setIsSectionParentView((prev) => !prev)}
                            >
                                {isSectionParentView
                                    ? t('TEXT_SWITCH_INTEREST_RATE_VIEW_FOR_SECTIONED_CHILD_PROPERTY', 'Se seksjonen')
                                    : t('TEXT_SWITCH_INTEREST_RATE_VIEW_FOR_SECTIONED_PROPERTY', 'Se grunneiendommen')}
                            </LoadingButton>
                        )}
                        {data?.delays?.length !== 0 && (
                            <LoadingButton
                                disableElevation
                                variant="contained"
                                color="accent"
                                type="button"
                                loading={isDownloadingPdf}
                                onClick={getPdfUrl}
                            >
                                {t('TEXT_DOWNLOAD_PDF', 'Last ned som PDF')}
                            </LoadingButton>
                        )}
                    </ActionWrapper>
                )
            }
        />
    )
}

const ActionWrapper = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}))
